import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import StopRoundedIcon from '@mui/icons-material/StopRounded';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import type { SxProps, Theme } from '@mui/material/styles';
import {
  type KeyboardEvent,
  type MouseEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import {
  MAX_RECORDING_SECONDS,
  RECORDING_WARNING_SECONDS,
  type VoicePreview,
  type VoiceRecorder,
  WAVEFORM_BARS,
} from './useVoiceRecorder';

const iconButtonSx: SxProps<Theme> = {
  height: 40,
  width: 40,
  flexShrink: 0,
  p: 0,
  color: 'text.secondary',
  '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
  '& svg': { fontSize: 22 },
};

const pillSx: SxProps<Theme> = {
  display: 'flex',
  height: 44,
  minWidth: 0,
  flex: 1,
  alignItems: 'center',
  gap: 1.25,
  borderRadius: '22px',
  border: 1,
  borderColor: 'divider',
  bgcolor: 'background.default',
  pl: 1.5,
  pr: 1.5,
};

export const formatClock = (totalSeconds: number): string => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.floor(totalSeconds % 60);
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

interface WaveformProps {
  levels: number[];
  progress?: number;
  onSeek?: (fraction: number) => void;
  label: string;
}

const Waveform = ({
  levels,
  progress,
  onSeek,
  label,
}: WaveformProps): JSX.Element => {
  const bars = [
    ...Array.from(
      { length: Math.max(0, WAVEFORM_BARS - levels.length) },
      () => 0
    ),
    ...levels.slice(-WAVEFORM_BARS),
  ];

  const seekFromPointer = (event: MouseEvent<HTMLElement>): void => {
    if (!onSeek) return;
    const rect = event.currentTarget.getBoundingClientRect();
    onSeek(Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width)));
  };
  const seekFromKeyboard = (event: KeyboardEvent<HTMLElement>): void => {
    if (!onSeek || progress == null) return;
    const step =
      event.key === 'ArrowRight' ? 0.05 : event.key === 'ArrowLeft' ? -0.05 : 0;
    if (step) {
      event.preventDefault();
      onSeek(Math.min(1, Math.max(0, progress + step)));
    }
  };

  return (
    <Box
      role={onSeek ? 'slider' : 'img'}
      aria-label={label}
      aria-valuemin={onSeek ? 0 : undefined}
      aria-valuemax={onSeek ? 100 : undefined}
      aria-valuenow={onSeek ? Math.round((progress ?? 0) * 100) : undefined}
      tabIndex={onSeek ? 0 : undefined}
      onClick={seekFromPointer}
      onKeyDown={seekFromKeyboard}
      sx={{
        display: 'flex',
        height: 28,
        minWidth: 0,
        flex: 1,
        alignItems: 'center',
        gap: '2px',
        cursor: onSeek ? 'pointer' : 'default',
        borderRadius: 1,
        '&:focus-visible': {
          outline: '2px solid',
          outlineColor: 'primary.main',
          outlineOffset: 2,
        },
      }}
    >
      {bars.map((level, index) => {
        const played = progress != null && index / bars.length < progress;
        return (
          <Box
            // Bars are positional; the index is the identity
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            component="span"
            sx={{
              flex: 1,
              minWidth: 2,
              maxWidth: 4,
              height: `${Math.max(12, Math.round(level * 100))}%`,
              borderRadius: 2,
              bgcolor:
                progress == null || played ? 'primary.main' : 'text.disabled',
              transition: 'height 80ms linear',
            }}
          />
        );
      })}
    </Box>
  );
};

interface RecordingBarProps {
  recorder: VoiceRecorder;
}

export const RecordingBar = ({ recorder }: RecordingBarProps): JSX.Element => {
  const { t } = useTranslation();
  const remaining = MAX_RECORDING_SECONDS - recorder.elapsedSeconds;
  const nearLimit = remaining <= RECORDING_WARNING_SECONDS;

  return (
    <>
      <IconButton
        sx={iconButtonSx}
        aria-label={t('chat.deleteRecording')}
        size="small"
        onClick={recorder.discard}
      >
        <DeleteOutlinedIcon />
      </IconButton>
      <Box sx={pillSx}>
        <Box
          component="span"
          aria-hidden="true"
          sx={{
            height: 8,
            width: 8,
            flexShrink: 0,
            borderRadius: '50%',
            bgcolor: 'error.main',
            animation: 'r8tr-web-rec-pulse 1.2s ease-in-out infinite',
            '@keyframes r8tr-web-rec-pulse': {
              '50%': { opacity: 0.3 },
            },
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
          }}
        />
        <Waveform levels={recorder.levels} label={t('chat.recordingLevel')} />
        <Box
          component="span"
          role="timer"
          aria-live="off"
          sx={{
            flexShrink: 0,
            fontSize: '0.8rem',
            fontVariantNumeric: 'tabular-nums',
            color: nearLimit ? 'error.main' : 'text.primary',
            fontWeight: nearLimit ? 700 : 400,
          }}
        >
          {nearLimit
            ? t('chat.secondsLeft', { count: Math.max(0, remaining) })
            : formatClock(recorder.elapsedSeconds)}
        </Box>
      </Box>
      <IconButton
        sx={{ ...iconButtonSx, color: 'primary.main' }}
        aria-label={t('chat.stopRecording')}
        size="small"
        onClick={recorder.stop}
      >
        <StopRoundedIcon />
      </IconButton>
    </>
  );
};

interface PreviewBarProps {
  preview: VoicePreview;
  onDiscard: () => void;
  onSend: () => void;
}

export const PreviewBar = ({
  preview,
  onDiscard,
  onSend,
}: PreviewBarProps): JSX.Element => {
  const { t } = useTranslation();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const duration = preview.durationSeconds;

  useEffect(() => {
    const audio = new Audio(preview.url);
    audioRef.current = audio;
    const handleTime = (): void => setCurrentTime(audio.currentTime);
    const handleEnd = (): void => {
      setPlaying(false);
      setCurrentTime(0);
    };
    audio.addEventListener('timeupdate', handleTime);
    audio.addEventListener('ended', handleEnd);
    return () => {
      audio.pause();
      audio.removeEventListener('timeupdate', handleTime);
      audio.removeEventListener('ended', handleEnd);
      audioRef.current = null;
    };
  }, [preview.url]);

  const togglePlay = (): void => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
      setPlaying(false);
    } else {
      void audio.play().then(() => setPlaying(true));
    }
  };

  const seek = (fraction: number): void => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = fraction * duration;
    setCurrentTime(audio.currentTime);
  };

  return (
    <>
      <IconButton
        sx={iconButtonSx}
        aria-label={t('chat.deleteRecording')}
        size="small"
        onClick={onDiscard}
      >
        <DeleteOutlinedIcon />
      </IconButton>
      <Box sx={pillSx}>
        <IconButton
          size="small"
          aria-label={playing ? t('chat.pausePreview') : t('chat.playPreview')}
          onClick={togglePlay}
          sx={{
            height: 30,
            width: 30,
            flexShrink: 0,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            '&:hover': { bgcolor: 'primary.dark' },
          }}
        >
          {playing ? (
            <PauseRoundedIcon fontSize="small" />
          ) : (
            <PlayArrowRoundedIcon fontSize="small" />
          )}
        </IconButton>
        <Waveform
          levels={preview.peaks}
          progress={duration ? currentTime / duration : 0}
          onSeek={seek}
          label={t('chat.previewPosition')}
        />
        <Box
          component="span"
          sx={{
            flexShrink: 0,
            fontSize: '0.8rem',
            fontVariantNumeric: 'tabular-nums',
            color: 'text.secondary',
          }}
        >
          {formatClock(playing || currentTime > 0 ? currentTime : duration)}
        </Box>
      </Box>
      <IconButton
        sx={{ ...iconButtonSx, color: 'primary.main' }}
        aria-label={t('chat.sendRecording')}
        size="small"
        onClick={onSend}
      >
        <SendOutlinedIcon />
      </IconButton>
    </>
  );
};
